/**
 * Coque de l’app de contrôle d’accès (`/scan/*`) — SCAN-01.
 *
 * Le contexte dicte la forme : le hall, 45 minutes avant le lever de rideau,
 * un smartphone tenu à une main, souvent dans la pénombre. D’où le fond sombre
 * (moins éblouissant, meilleure autonomie, la caméra reste lisible), le plein
 * écran avec marges de sécurité pour l’encoche, et une seule barre d’en-tête
 * compacte pour laisser toute la hauteur au viseur.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ LE PIED DE PAGE, ET CE QU’IL N’A PLUS LE DROIT DE DIRE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Il portait, sur TOUS les écrans de contrôle : « le scan exige du réseau ;
 * imprimez la liste d’émargement avant l’ouverture des portes ». Deux lignes
 * plus haut, le rappel d’avant-scène — corrigé, lui — expliquait qu’un mode
 * hors ligne existe et que le contrôle continue sans réseau (HLG-01…HLG-06).
 * Les deux phrases s’affichaient **en même temps, sur le même écran**.
 *
 * C’est la troisième occurrence de la même famille de défaut dans ce dépôt
 * (la vitrine niait la boutique, cet écran-ci niait le hors ligne) : un texte
 * décrit fidèlement le produit tel qu’il était quand on l’a écrit, et le
 * calendrier le dépasse. La parade n’est pas de le réécrire mieux, c’est de
 * n’avoir **qu’un seul endroit** qui explique le hors ligne :
 *
 *   · le RAPPEL d’avant-scène (`features/scan/composants/RappelEmargement`)
 *     dit ce qu’est le mode hors ligne et ce qu’il coûte (HLG-04) ;
 *   · les RÉGLAGES (`horsligne/SectionPreparation`) le préparent, sciemment,
 *     avant la soirée (HLG-06) ;
 *   · le BANDEAU (`horsligne/BandeauHorsLigne`) dit son état et l’âge de la
 *     liste, en permanence, quand il est actif (HLG-03).
 *
 * Il ne reste donc ici que ce qui est vrai dans TOUS les cas — en ligne, hors
 * ligne, et appareil en panne : la liste d’émargement imprimée est le filet
 * ultime. Ce pied-là ne dépend d’aucune fonction livrée depuis, et ne peut
 * donc plus se périmer.
 */

import { Suspense } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'

import { useAuth, useUtilisateur } from '../auth'
import { CHEMINS } from '../lib/chemins'
import { useTheatre } from '../lib/theatre'
import { ChargementPage } from '../ui/Spinner'
import { BandeauEcheance, EcranTenantBloquant, useEtatTenantBloquant } from './EtatTenant'

export function LayoutScan() {
  const utilisateur = useUtilisateur()
  const { deconnecter } = useAuth()
  const naviguer = useNavigate()
  const { nom: nomTheatre } = useTheatre()
  const etatTenant = useEtatTenantBloquant()

  async function seDeconnecter(): Promise<void> {
    await deconnecter()
    await naviguer(CHEMINS.connexionStaff, { replace: true })
  }

  // LOT F3 — tenant expiré/suspendu : voir EtatTenant.tsx pour la justification.
  if (etatTenant !== undefined) return <EcranTenantBloquant etat={etatTenant} />

  return (
    <div className="ecran-scan flex flex-col">
      {/* Sombre : seule coque à fond noir (contrôle d'accès dans le hall). */}
      <BandeauEcheance sombre />

      <header className="flex items-center gap-3 border-b border-slate-800 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{nomTheatre}</p>
          <p className="text-xs text-slate-400">Contrôle d’accès</p>
        </div>

        {/* `min-h-11` : cet écran se manipule debout, dans le noir, souvent
            d’une seule main — aucune cible ne descend sous 44 px. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Link
            to={CHEMINS.admin}
            className="flex min-h-11 items-center rounded-md px-3 text-sm text-slate-300 transition-colors hover:bg-slate-800 active:bg-slate-800"
          >
            Back-office
          </Link>
          <button
            type="button"
            onClick={() => { void seDeconnecter() }}
            className="flex min-h-11 items-center rounded-md bg-slate-800 px-4 text-sm font-medium text-slate-100 transition-colors hover:bg-slate-700 active:bg-slate-700"
          >
            Quitter
          </button>
        </div>
      </header>

      {/* La session dure 12 h (A-04) : on tient la soirée sans reconnexion. */}
      <main className="flex min-h-0 flex-1 flex-col">
        <Suspense fallback={<ChargementPage libelle="Chargement de l’écran…" />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="px-4 py-2 text-center text-xs text-slate-400">
        {utilisateur.nom} · gardez la liste d’émargement imprimée sous la main&nbsp;: c’est le
        filet ultime
      </footer>
    </div>
  )
}
